import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { entryDuration, entryEarnings, formatMoney, logEntry, overlappingEntries, type TimeEntry } from "@otrack/domain";
import { Tooltip } from "@otrack/ui";
import { useGoogleView } from "./calendar/google";
import { collectItems, colorOf } from "./calendar/model";
import { projectLabel } from "./commands";
import { useApp, useDataset, useFactory } from "./context";
import { axisFraction, strataAxis, strataRows, type StrataAxis } from "./strata-layout";
import { dayRange, formatClock, formatDuration, HOUR, MINUTE } from "./time";

const SNAP = 5 * MINUTE;
const LABEL_WIDTH = 188;
const TOTAL_WIDTH = 72;

interface Drag { projectId: string | null; track: HTMLElement; anchor: number; current: number }

function snap(ts: number): number {
  return Math.round(ts / SNAP) * SNAP;
}

function timeAt(axis: StrataAxis, track: HTMLElement, clientX: number, now: number): number {
  const rect = track.getBoundingClientRect();
  const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  return Math.min(now, snap(axis.from + fraction * (axis.to - axis.from)));
}

function entryTitle(label: string, entry: TimeEntry, now: number): string {
  const earned = entryEarnings(entry, now);
  return [
    label,
    entry.description,
    `${formatClock(entry.start)} to ${entry.end === null ? "now" : formatClock(entry.end)}, ${formatDuration(entryDuration(entry, now))}`,
    earned ? formatMoney(earned) : entry.billable ? "" : "Non-billable"
  ].filter(Boolean).join("\n");
}

export function Strata() {
  const { now, act, notify, openEntry, google } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const day = dayRange(now);
  const googleView = useGoogleView(google, dataset.settings);
  // Today's plans, Google's too, show faintly on their Project's layer, so the day reads as plan and actual.
  const minute = Math.floor(now / 60_000);
  const plans = useMemo(() => collectItems(dataset, googleView, day.from, day.to, now).plans.filter((plan) => !plan.allDay), [dataset, googleView, day.from, day.to, minute]);
  const rows = strataRows(dataset, day, 4, plans.map((plan) => plan.projectId));
  const axis = strataAxis(rows, day, now, plans);
  const hours: number[] = [];
  for (let ts = axis.from; ts <= axis.to; ts += HOUR) hours.push(ts);
  const span = (start: number, end: number) => ({ left: `${axisFraction(axis, start) * 100}%`, width: `${(axisFraction(axis, end) - axisFraction(axis, start)) * 100}%` });

  const update = (next: Drag | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>, projectId: string | null) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest("[data-entry]")) return;
    const track = event.currentTarget;
    const at = timeAt(axis, track, event.clientX, now);
    track.setPointerCapture(event.pointerId);
    update({ projectId, track, anchor: at, current: at });
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = dragRef.current;
    if (current) update({ ...current, current: timeAt(axis, current.track, event.clientX, now) });
  };
  const onPointerUp = () => {
    const current = dragRef.current;
    update(null);
    if (!current) return;
    const start = Math.min(current.anchor, current.current), end = Math.max(current.anchor, current.current);
    if (end - start < 10 * MINUTE) return;
    if (!current.projectId) return notify("Pick a project layer to add time to");
    if (overlappingEntries(dataset, start, end, now).length) return notify("That overlaps other time");
    void act(logEntry(dataset, { projectId: current.projectId }, start, end, factory), `Added ${formatDuration(end - start)} to ${projectLabel(dataset, current.projectId)}, ${formatClock(start)} to ${formatClock(end)}`);
  };

  const nowVisible = now >= axis.from && now <= axis.to;
  const nowAt = axisFraction(axis, now);
  const dayTotal = rows.reduce((sum, row) => sum + row.entries.reduce((rowSum, entry) => rowSum + entryDuration(entry, now), 0), 0);
  const columns = { gridTemplateColumns: `${LABEL_WIDTH}px minmax(0, 1fr) ${TOTAL_WIDTH}px` };

  return <section className="ot-strata" aria-label="Your day">
    <div className="ot-strata__head">
      <h2>Your day <span className="ot-mono">{formatDuration(dayTotal)}</span></h2>
      <ul className="ot-legend" aria-label="Legend">
        <li><i className="ot-legend__logged"/>Logged</li>
        {plans.length > 0 && <li><i className="ot-legend__planned"/>Planned</li>}
        <li><i className="ot-legend__unbilled"/>Non-billable</li>
        <li><i className="ot-legend__running"/>Running</li>
      </ul>
    </div>
    <div className="ot-strata__plot" style={{ ["--ot-label-width" as string]: `${LABEL_WIDTH}px`, ["--ot-total-width" as string]: `${TOTAL_WIDTH}px` }}>
      <div className="ot-strata__axis" style={columns}>
        <div/>
        <div className="ot-strata__ticks">{hours.map((ts) => {
          // The Now label takes the place of any hour it would collide with.
          const hidden = nowVisible && rows.length > 0 && Math.abs(axisFraction(axis, ts) - nowAt) < 0.04;
          return <span key={ts} className={hidden ? "is-hidden" : ""} style={{ left: `${axisFraction(axis, ts) * 100}%` }}>{formatClock(ts).slice(0, 2)}</span>;
        })}</div>
        <div/>
      </div>
      <div className="ot-strata__field" aria-hidden="true">
        {hours.map((ts) => <i key={ts} className="ot-strata__hour" style={{ left: `${axisFraction(axis, ts) * 100}%` }}/>)}
        {nowVisible && rows.length > 0 && <div className="ot-strata__future" style={{ left: `${nowAt * 100}%` }}/>}
      </div>
      <div className="ot-strata__rows">
        {rows.length === 0 && <div className="ot-strata__empty">Nothing tracked today. Start a timer from the bar above.</div>}
        {rows.map((row, rowIndex) => {
          const rowTotal = row.entries.reduce((sum, entry) => sum + entryDuration(entry, now), 0);
          const rowPlans = plans.filter((plan) => plan.projectId === row.projectId);
          return <div className="ot-strata__row" key={row.projectId ?? "none"} style={{ ...columns, ["--ot-row" as string]: rowIndex, ["--pc" as string]: colorOf(dataset, row.projectId) }}>
            <div className="ot-strata__who"><strong><i className="ot-swatch" aria-hidden="true"/>{row.title}</strong>{row.subtitle && <span>{row.subtitle}</span>}</div>
            <div
              className="ot-strata__track"
              onPointerDown={(event) => onPointerDown(event, row.projectId)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={() => update(null)}
            >
              {rowPlans.map((plan) => {
                const start = Math.max(plan.start, axis.from), end = Math.min(plan.end, axis.to);
                return end > start ? <div key={plan.key} className="ot-strata__plan" style={span(start, end)} aria-hidden="true"/> : null;
              })}
              {row.entries.map((entry) => {
                const start = Math.max(entry.start, axis.from), end = Math.min(entry.end ?? now, axis.to);
                if (end <= start) return null;
                const classes = ["ot-band", entry.end === null ? "ot-band--running" : "", entry.billable ? "" : "ot-band--unbilled"].join(" ");
                const roomy = (end - start) / (axis.to - axis.from) > 0.05;
                return <Tooltip key={entry.id} label={entryTitle(projectLabel(dataset, entry.projectId), entry, now)} delay={180}>
                  <button
                    type="button"
                    data-entry={entry.id}
                    className={classes}
                    style={span(start, end)}
                    aria-label={entryTitle(projectLabel(dataset, entry.projectId), entry, now).replaceAll("\n", ", ")}
                    onClick={() => openEntry(entry.id)}
                  >{roomy && <span>{entry.description}</span>}</button>
                </Tooltip>;
              })}
              {drag && drag.track && drag.projectId === row.projectId && <div className="ot-strata__ghost" style={span(Math.min(drag.anchor, drag.current), Math.max(drag.anchor, drag.current))}>
                <span className="ot-mono">{formatDuration(Math.abs(drag.current - drag.anchor))}</span>
              </div>}
            </div>
            <div className={`ot-strata__total ot-mono ${rowTotal ? "" : "is-zero"}`}>{rowTotal ? formatDuration(rowTotal) : "0m"}</div>
          </div>;
        })}
      </div>
      {nowVisible && rows.length > 0 && <div className="ot-strata__now" style={{ left: `calc(var(--ot-plot-pad) + var(--ot-label-width) + (100% - 2 * var(--ot-plot-pad) - var(--ot-label-width) - var(--ot-total-width)) * ${nowAt})` }} aria-hidden="true">
        <span className="ot-mono">{formatClock(now)}</span>
      </div>}
    </div>
    <p className="ot-strata__hint">Drag across a layer to add time to that project.</p>
  </section>;
}
